import { useToast } from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { BodyOf, Messages, ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import useUploadFiles, { S3Object } from "../../../shared/hooks/useUploadFiles";
import { queryKeys } from "../../../shared/query-keys";
import {
  CommCenterEmailMessageAttachmentId,
  CommCenterEmailMessageId,
} from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { optimisticUpdate } from "../../../shared/utils/optimistic-update";
import {
  CreateEmailMessageRequest,
  SubmitEmailMessage,
} from "../pages/CommunicationCenterTicket/CommunicationCenterTicketRoute";
import { buildAttachmentsPayloadForEmailMessage } from "../utils/comm-center-email-utils";
import { DefaultAttachment } from "../components/Emails/NewEmailTicket";

interface Params {
  onSubmitEmailMessage?: () => void;
  onSuccessCreateEmailTicket: (response: ResponseOf<"post", "./comm_center/email/threads">) => void;
  defaultAttachments?: DefaultAttachment[];
}

export default function useEmailTicketMessages(params: Params) {
  const toast = useToast();
  const { api } = useApi();
  const queryClient = useQueryClient();
  const [attachments, setAttachments] = React.useState<S3Object[]>([]);
  const copiedAttachmentsStartedUploading = React.useRef(false);

  const {
    uploads,
    isUploading,
    setUploads,
    upload: uploadToS3,
    attachFiles,
  } = useUploadFiles({
    fileUploadDestination: { by: "email-attachment" },
    multiple: true,
    uploads: [],
    onFileUploadSuccess: (upload) => {
      if (upload.type === "s3-object") {
        setAttachments((prev) => [...prev, upload]);
      }
    },
    onFileUploadError: (error) => {
      toast({
        title: "Could not upload attachments",
        description: formatErrorResponse(error),
        status: "error",
      });
    },
  });

  const downloadFilesFromS3 = useMutation({
    mutationFn: async (params: { docs: DefaultAttachment[] }) => {
      return await Promise.all(
        params.docs.map(async (doc) => {
          const response = await fetch(doc.s3Url);
          const blob = await response.blob();
          const file = new File([blob], doc.name, { type: doc.type });
          return { ...doc, file };
        })
      );
    },
    onSuccess: (docs) => {
      const newUploads = attachFiles(docs.map((doc) => doc.file));
      if (newUploads && newUploads.length > 0) {
        uploadToS3(newUploads);
      }
    },
    onError: (error) => {
      toast({
        title: "Could not upload attachments",
        description: formatErrorResponse(error),
        status: "error",
      });
    },
  });

  React.useEffect(() => {
    if (
      copiedAttachmentsStartedUploading.current ||
      params.defaultAttachments === undefined ||
      params.defaultAttachments.length === 0
    ) {
      return;
    }

    copiedAttachmentsStartedUploading.current = true;
    downloadFilesFromS3.mutate({ docs: params.defaultAttachments ?? [] });

    return () => {
      downloadFilesFromS3.reset();
      setUploads([]);
    };
  }, [downloadFilesFromS3, params.defaultAttachments, setUploads]);

  const submitEmailMessage = useMutation({
    mutationFn: async (createParams: CreateEmailMessageRequest) => {
      return api.post("./comm_center/email/threads/:threadId/messages", {
        path: {
          threadId: createParams.threadId,
        },
        body: {
          attachments: createParams.attachments,
          content: createParams.content,
          contentPlainText: createParams.contentPlainText,
          recipients: createParams.recipients,
          sender: createParams.sender,
          cc: createParams.cc,
          bcc: createParams.bcc,
        },
      });
    },
    onMutate: async (createParams) => {
      const tickets = optimisticUpdate<{ tickets: Messages["CommCenterTicket"][] }>({
        queryClient,
        queryKey: queryKeys.commCenter.search.K,
        update: (draft) => {
          const ticket = draft.tickets.find((ticket) => ticket.id === createParams.ticketId);

          if (ticket !== undefined) {
            mutateSubmitEmailMessage({ ticket, body: createParams });
          }
        },
      });

      const ticket = optimisticUpdate<{ ticket: Messages["CommCenterTicket"] }>({
        queryClient,
        queryKey: queryKeys.commCenter.get(createParams.ticketId),
        update: (draft) => mutateSubmitEmailMessage({ ticket: draft.ticket, body: createParams }),
      });

      return { tickets, ticket };
    },
    onError: (error, _newChatMessage, context) => {
      queryClient.setQueryData(
        queryKeys.commCenter.get(_newChatMessage.ticketId),
        context?.ticket.previousValue
      );

      toast({
        title: "Could not send message.",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
    onSuccess: () => {
      toast({
        title: "Message sent successfully",
        status: "success",
        position: "top-right",
      });

      params.onSubmitEmailMessage?.();
    },
  });

  function mutateSubmitEmailMessage(params: {
    ticket: Messages["CommCenterTicket"];
    body: CreateEmailMessageRequest;
  }) {
    const { ticket, body } = params;
    const messageId = CommCenterEmailMessageId.parse(Instant.now().toEpochMilli() * -1);
    ticket.emailThread?.messages.push({
      id: messageId,
      sender: body.sender,
      cc: body.cc,
      bcc: body.bcc,
      recipients: body.recipients,
      content: body.content,
      contentPlainText: body.contentPlainText,
      direction: "OUTBOUND",
      threadId: body.threadId,
      replyTo: null,
      s3Url: null,
      attachments: attachments.map((attachment) => ({
        id: CommCenterEmailMessageAttachmentId.parse(Instant.now().toEpochMilli() * -1),
        type: attachment.file.type,
        s3Url: URL.createObjectURL(attachment.file),
        name: attachment.file.name,
        messageId,
        createdAt: Instant.now(),
      })),
      createdAt: Instant.now(),
      readAt: null,
    });
  }

  const handleSubmitNewEmailMessage = async (body: SubmitEmailMessage) => {
    const attachmentsPayload = buildAttachmentsPayloadForEmailMessage(attachments);
    await submitEmailMessage.mutateAsync({ ...body, attachments: attachmentsPayload });
    setAttachments([]);
  };

  const createEmailTicket = useMutation({
    mutationFn: (newEmailTicketRequest: BodyOf<"post", "./comm_center/email/threads">) => {
      return api.post("./comm_center/email/threads", {
        body: newEmailTicketRequest,
      });
    },
    onSuccess: (response) => {
      params.onSuccessCreateEmailTicket(response);
    },
    onError: (error) => {
      toast({
        title: "Could not create new ticket",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const handleAddAttachments = (newAttachments: S3Object[]) => {
    setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments]);
  };

  const handleRemoveAttachment = (attachment: S3Object) => {
    const newAttachments = attachments.filter((a) => a.file.name !== attachment.file.name);
    setAttachments(newAttachments);
  };

  return {
    attachments,
    onAddAttachments: handleAddAttachments,
    onRemoveAttachment: handleRemoveAttachment,
    submitEmailMessage: handleSubmitNewEmailMessage,
    createEmailTicket,
    isUploading,
    uploads,
    isDownloadingFiles: downloadFilesFromS3.isPending,
  };
}
